/**
 * **条码 / 二维码的 `path` 数据** —— 一维的条与二维的模块**共用这一处**。
 *
 * ## 为什么要有这个文件（它是 bug 的产物，不是洁癖）
 *
 * 同一条子路径（`M{x} {y}h{w}v{h}h{-w}z`）原来在**两个地方各写了一份**：
 *
 * ```text
 *   PageEditor.renderBarcode()   编辑视图：屏幕 px 的几何
 *   PageEditor.renderPreview()   精确预览：IR 的点 → px 的几何
 * ```
 *
 * 两处的数字来源不同（那是应该的：一个按物理尺寸画、一个按设备点画），
 * 但**拼字符串的规矩**是同一条 —— 而两份实现里有一份写错了：
 * 预览那份把 `string[]`（子路径数组）**直接塞进了 `d`**，
 * 于是 Vue 的 `setAttribute` 把它隐式 `join(',')`：
 *
 * ```text
 *   d="M37.83 37.83h13.24v1.89h-13.24z,M52.97 37.83h7.57v1.89h-7.57z,…"
 *                                    ↑ 这个逗号
 * ```
 *
 * SVG 的路径语法里，**命令组之间只允许空白**（逗号只能出现在一条命令的
 * 参数之间，见 `moveto-drawto-command-groups ::= … wsp* …`）——
 * 于是浏览器当场报 `Error: Problem parsing d="M356.65 173.08h22.03v3"`
 * （消息里引的是 `d` 的开头一截，所以看着"明明是对的"）。
 *
 * 症状很挑食：**二维码（2d）什么都不画，条码（1d）却是好的** ——
 * 因为一维那一份顺手 `.join('')` 了。两份实现迟早分叉，这就是代价。
 *
 * 所以这里收口成三个纯函数：
 *
 * ```text
 *   pathDataOf(rects)      一批"墨块" → 一条 `d`（★ 唯一拼字符串的地方）
 *   barsPathData(…)        一维：编码结果的条 → `d`
 *   matrixPathData(…)      二维：模块矩阵的"扫描线并块" → `d`
 * ```
 *
 * ⚠️ 单位是**画布单位**（编辑视图 = 屏幕 px；预览 = px）：调用方自己把
 * mm / 设备点换算好再进来 —— 这个文件不认识 mm、dpi、缩放，
 * 所以它是纯函数、可以在无 DOM 的脚本里断言（`verify:print` 就是这么验的）。
 */

/**
 * 一个"墨块"在**画布单位**下的矩形。
 *
 * ⚠️ 一维的一个条、二维里"一行的连续模块并起来的那一段"，都是这个形状 ——
 * 两者的差异只在**怎么数出来**（见下面两个函数），画出来是同一种东西。
 */
export interface InkRect {
  x: number
  y: number
  w: number
  h: number
}

/**
 * 一个墨块 → 一条闭合子路径。
 *
 * ⚠️ 保留两位小数（`toFixed(2)`）：一个二维码有几百个子路径，
 * 浮点全精度会让 `d` 长出好几倍（进 DOM 就是几万字符的属性），
 * 而两位小数在这个尺度上远小于一个设备点。
 *
 * ⚠️ 回程的横移是**同一个数取负**（不是拿坐标重算一遍）：两处各算一次
 * 会得到 `h22.03` 和 `h-22.04` 这种不对称的闭合，边上会多出半个点。
 */
function subpath(rect: InkRect): string {
  const w = rect.w.toFixed(2)
  return `M${rect.x.toFixed(2)} ${rect.y.toFixed(2)}h${w}v${rect.h.toFixed(2)}h${(-rect.w).toFixed(2)}z`
}

/**
 * 一批墨块 → 一条 `path` 的 `d`。
 *
 * ★★ 返回值**必须是字符串**：这个函数的返回值会被整个塞进 `d` 属性，
 * 而只要它是个数组，Vue 就会替你 `join(',')` —— 那正是上面文件头记的那个
 * 浏览器报错（`verify:print` 有一条断言直接查"`d` 里没有逗号"）。
 */
export function pathDataOf(rects: readonly InkRect[]): string {
  let d = ''
  for (const rect of rects) d += subpath(rect)
  return d
}

/** 一维条码：一个条（模块坐标 + 模块宽）在画布上的落点 */
export interface BarsPathOptions {
  /** 条：**内容区坐标**（模块数），和 `EncodedSymbol.bars` 同形 */
  bars: readonly { x: number; w: number }[]
  /** 内容区左边缘（画布单位）—— 静区由调用方加进来 */
  originX: number
  /** 条的顶边（画布单位） */
  originY: number
  /** 一个模块 = 多少画布单位（编辑视图 = 屏幕 px；预览 = 每个设备点的 px） */
  moduleSize: number
  /** 条高（画布单位） */
  height: number
}

/** 一维：条 → `d` */
export function barsPathData(options: BarsPathOptions): string {
  const { bars, originX, originY, moduleSize, height } = options
  return pathDataOf(
    bars.map((bar) => ({
      x: originX + bar.x * moduleSize,
      y: originY,
      w: bar.w * moduleSize,
      h: height,
    })),
  )
}

/**
 * 二维的**扫描线并块**：每一行里连续的模块并成一个矩形。
 *
 * 二维码一半以上的模块是连片的，并块之后子路径数少一个数量级；
 * 而且一次扫描就能生成，不需要先建一张位图。
 *
 * ⚠️ `ink` 是个**取值函数**而不是矩阵本身：IR 里的矩阵是"每行一个 `'0'/'1'`
 * 字符串"（可 diff、体积小），而编辑视图那边拿到的是扁平的 `boolean[]` ——
 * 两种表示只是取法不同，**不值得各写一份并块逻辑**（二分叉就是上面那个 bug）。
 */
export function matrixRuns(options: {
  rows: number
  cols: number
  ink: (row: number, col: number) => boolean
}): { row: number; col: number; span: number }[] {
  const { rows, cols, ink } = options
  const runs: { row: number; col: number; span: number }[] = []
  for (let row = 0; row < rows; row += 1) {
    let start = -1
    /* ⚠️ 走到 `cols`（多一格）：这样行尾那一段也会被收掉，不用在循环外补一遍 */
    for (let col = 0; col <= cols; col += 1) {
      const on = col < cols && ink(row, col)
      if (on && start < 0) start = col
      if (!on && start >= 0) {
        runs.push({ row, col: start, span: col - start })
        start = -1
      }
    }
  }
  return runs
}

/** 二维：模块矩阵 → `d` */
export function matrixPathData(options: {
  rows: number
  cols: number
  ink: (row: number, col: number) => boolean
  /** 矩阵**左上角**（画布单位）—— 静区由调用方加进来 */
  originX: number
  originY: number
  /** 一个模块 = 多少画布单位（正方形：宽高同一个数） */
  moduleSize: number
}): string {
  const { originX, originY, moduleSize } = options
  return pathDataOf(
    matrixRuns(options).map((run) => ({
      x: originX + run.col * moduleSize,
      y: originY + run.row * moduleSize,
      w: run.span * moduleSize,
      h: moduleSize,
    })),
  )
}
